import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { recuperarCotizacion, agregarCarroProducto } from "../../services/backend/cartService";
import { actualizarEntrega } from "../../services/backend/deliveryService";
import { mapDetalleToCartItems } from "../../services/transforms/cartDataTransforms";
import { getAffariClienteCodigo } from "../../services/backend/authService";
import { StorageKeys, session } from "../../utils/storage";
import { getCartLineKey, normalizeWarehouse } from "../../features/cart/utils/cartLineKey";
import { getCompany } from "../../config/companyConfig";
import logger from "../../shared/utils/logger";

const CartContext = createContext();
export const useCartContext = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartResumen, setCartResumen] = useState(null);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [cotizacionId, setCotizacionId] = useState(() => {
    try {
      return String(session.get(StorageKeys.COTIZACION_ID) || "").trim() || null;
    } catch { return null; }
  });

  const loadCotizacionFromBackend = useCallback(async (almacen) => {
    setIsCartLoading(true);
    try {
      almacen = almacen || getCompany().defaultWarehouse;
      const cliente = getAffariClienteCodigo();

      if (!cliente) {
        return null;
      }

      const response = await recuperarCotizacion(almacen, cliente);

      if (!response?.cotizacion?.id) {
        setCartItems([]);
        setCartResumen(null);
        setCotizacionId(null);
        session.remove(StorageKeys.COTIZACION_ID);
        return null;
      }

      const id = String(response.cotizacion.id);
      session.set(StorageKeys.COTIZACION_ID, id);
      setCotizacionId(id);

      //setear entrega a retiro para no cobrar el despacho antes del checkout
      await actualizarEntrega(id, 3);

      const detalles = Array.isArray(response.detalles) ? response.detalles : [];
      const cartItemsFromBackend = mapDetalleToCartItems({ detalles });
      setCartItems(cartItemsFromBackend);

      const resumen = response.resumen || null;
      setCartResumen(resumen);

      return { id, detalles: cartItemsFromBackend, resumen };
    } catch (error) {
      logger.error("Error al recuperar cotización:", error);
      return null;
    } finally {
      setIsCartLoading(false);
    }
  }, []);

  const addToCart = useCallback(async (item, quantity = 1) => {
    if (!cotizacionId) {
      return { success: false, error: "No hay cotización activa" };
    }

    try {
      const response = await agregarCarroProducto(quantity, item.code, cotizacionId);

      if (response?.detalles) {
        const cartItemsFromBackend = mapDetalleToCartItems({ detalles: response.detalles });
        setCartItems(cartItemsFromBackend);
      }

      if (response?.resumen) {
        setCartResumen(response.resumen);
      }

      return { success: true };
    } catch (error) {
      logger.error("Error al agregar producto:", error);
      return { success: false, error: error?.message || "Error al agregar producto" };
    }
  }, [cotizacionId]);

  const updateCartQuantityFromBackend = useCallback(async (productCode, quantity) => {
    if (!cotizacionId) {
      return { success: false, error: "No hay cotización activa" };
    }

    try {
      const response = await agregarCarroProducto(quantity, productCode, cotizacionId);

      if (response?.detalles) {
        const cartItemsFromBackend = mapDetalleToCartItems({ detalles: response.detalles });
        setCartItems(cartItemsFromBackend);
      }

      if (response?.resumen) {
        setCartResumen(response.resumen);
      }

      return { success: true };
    } catch (error) {
      logger.error("Error al actualizar cantidad:", error);
      return { success: false, error: error?.message || "Error al actualizar cantidad" };
    }
  }, [cotizacionId]);

  const clearCart = useCallback(() => setCartItems([]), []);

  const resetCart = useCallback(() => {
    setCartItems([]);
    setCartResumen(null);
    setCotizacionId(null);
    session.remove(StorageKeys.COTIZACION_ID);
  }, []);

  const replaceCartItems = useCallback((items = []) => {
    const incoming = (Array.isArray(items) ? items : []).map((item) => {
      const normalizedItem = {
        ...item,
        warehouse: normalizeWarehouse(item.warehouse),
      };
      return { ...normalizedItem, lineKey: getCartLineKey(normalizedItem) };
    });

    setCartItems((prev) => {
      const incomingKeys = new Set(incoming.map((it) => it.lineKey));
      const localOnly = prev.filter((it) => !incomingKeys.has(it.lineKey));
      return [...incoming, ...localOnly];
    });
  }, []);

  const cartCount = useMemo(
    () => {
      if (cartResumen?.lineas != null) {
        return Number(cartResumen.lineas) || 0;
      }
      return cartItems.reduce((acc, item) => acc + item.quantity, 0);
    },
    [cartResumen, cartItems]
  );

  const value = useMemo(() => ({
    cartItems,
    cartResumen,
    cotizacionId,
    isCartLoading,
    cartCount,
    addToCart,
    updateCartQuantityFromBackend,
    clearCart,
    resetCart,
    replaceCartItems,
    loadCotizacionFromBackend,
  }), [
    cartItems,
    cartResumen,
    cotizacionId,
    isCartLoading,
    cartCount,
    addToCart,
    updateCartQuantityFromBackend,
    clearCart,
    resetCart,
    replaceCartItems,
    loadCotizacionFromBackend,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
